import { useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Button,
  useMediaQuery,
  useTheme,
  Link,
  Grid,
  CardActionArea,
  Typography,
  Card,
  Box,
} from '@mui/material'
import ControlPointIcon from '@mui/icons-material/ControlPoint'

import { HelperWrapper, MobileBottomLayout, PaginationButtons } from 'src/components'
import { useAccount, useMe } from 'src/hooks'
import { AccountRelationsCountKeys, EAccountSubscriptionPlans } from 'src/types'
import {
  ROUTES,
  SUBSCRIPTION_MAX_FREE_PRODUCTS_LIMIT,
  SUBSCRIPTION_MAX_PLUS_PRODUCTS_LIMIT,
} from 'src/constants'
import { joinAbsRoute } from 'src/common/utils'

import { useStyles } from './styles'

interface IProps {
  view?: 'grid' | 'table'
  upgrateTextLT: string
  entityCountKey: AccountRelationsCountKeys
  routeToAdd?: string
  showUpgrade?: boolean
}

export const AddEntityBtn = ({
  upgrateTextLT,
  entityCountKey,
  view = 'table',
  routeToAdd,
  showUpgrade = false,
}: IProps) => {
  const { data: account, isLoading: isAccountLoading } = useAccount()
  const { data: me, isLoading: isMeLoading } = useMe()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'))

  const handleAdd = () => {
    navigate('add')
  }

  const enterprisePlan = account?.subscription_plan === EAccountSubscriptionPlans.enterprise

  const upgradePremium =
    account?.subscription_plan === EAccountSubscriptionPlans.plus &&
    account?.[entityCountKey] >= SUBSCRIPTION_MAX_PLUS_PRODUCTS_LIMIT

  const upgradePlus =
    account?.subscription_plan === EAccountSubscriptionPlans.free &&
    account?.[entityCountKey] >= SUBSCRIPTION_MAX_FREE_PRODUCTS_LIMIT

  const isUserSuspended = me?.suspended || isMeLoading

  const isDisabled = upgradePlus || upgradePremium || isAccountLoading || isUserSuspended

  const classes = useStyles({ isDisabled: isDisabled })

  return (
    <>
      {view === 'table' ? (
        <div className={classes.addBtn}>
          <Button
            disabled={isDisabled && !enterprisePlan}
            color={'primary'}
            size={isMobile ? 'small' : 'large'}
            variant="contained"
            startIcon={<ControlPointIcon />}
            onClick={handleAdd}
            style={{
              marginLeft: isMobile ? 0 : 180,
              width: isMobile ? '95%' : 175,
              textWrap: 'nowrap',
            }}
          >
            <HelperWrapper t="button-add-new-text" isColorInherited />
          </Button>
          {(upgradePlus || upgradePremium) && (
            <div
              style={{
                color: theme.palette.custom.darkBlue2,
                fontSize: 14,
                marginLeft: isMobile ? 0 : 10,
                width: '95%',
              }}
            >
              <HelperWrapper t={upgrateTextLT} />
              &nbsp;
              <Link
                component={RouterLink}
                to={joinAbsRoute(ROUTES.ACCOUNTS, account?.id, `${ROUTES.ACCOUNT}?tab=billing`)}
              >
                <HelperWrapper t="upgrade-button-text" />
              </Link>
            </div>
          )}
        </div>
      ) : (
        <>
          {isMobile ? (
            <MobileBottomLayout>
              <PaginationButtons buttonsOnly />
              {routeToAdd && (
                <div className={classes.addNewButtonWrapper}>
                  {showUpgrade && (upgradePlus || upgradePremium) && (
                    <div
                      style={{
                        color: theme.palette.custom.darkBlue2,
                        fontSize: 14,
                        width: '100%',
                      }}
                    >
                      <HelperWrapper t="product-create-upgrade-text" />
                      &nbsp;
                      <Link
                        component={RouterLink}
                        to={joinAbsRoute(
                          ROUTES.ACCOUNTS,
                          account?.id,
                          `${ROUTES.ACCOUNT}?tab=billing`,
                        )}
                      >
                        <HelperWrapper t="upgrade-button-text" />
                      </Link>
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={handleAdd}
                    variant="contained"
                    color="secondary"
                    className={classes.addNewButton}
                    disabled={
                      showUpgrade
                        ? upgradePlus || upgradePremium || isAccountLoading
                        : isAccountLoading
                    }
                  >
                    <HelperWrapper t="button-add-new-text" isColorInherited />
                  </Button>
                </div>
              )}
            </MobileBottomLayout>
          ) : routeToAdd ? (
            <Grid item>
              <Card className={classes.addNewCardWrapper}>
                <CardActionArea
                  className={classes.addNewCard}
                  onClick={handleAdd}
                  disabled={isDisabled}
                >
                  <div className={classes.addNewCardAction}>
                    <ControlPointIcon
                      sx={{
                        color: isDisabled
                          ? theme.palette.custom.grey2
                          : theme.palette.custom.primary,
                      }}
                    />
                    <Typography className={classes.addNewCardText}>
                      <HelperWrapper
                        t="button-add-new-text"
                        isColorInherited
                        verticalPosition={5}
                      />
                    </Typography>
                  </div>
                  {showUpgrade && (upgradePlus || upgradePremium) && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography className={classes.addNewCardTextSecondary}>
                        <HelperWrapper t={upgrateTextLT} />
                      </Typography>
                      <Link
                        component={RouterLink}
                        to={joinAbsRoute(
                          ROUTES.ACCOUNTS,
                          account?.id,
                          `${ROUTES.ACCOUNT}?tab=billing`,
                        )}
                        sx={{ pointerEvents: 'auto' }}
                      >
                        <HelperWrapper t="upgrade-button-text" />
                      </Link>
                    </Box>
                  )}
                </CardActionArea>
              </Card>
            </Grid>
          ) : null}
        </>
      )}
    </>
  )
}
